import { Controller } from "@hotwired/stimulus";
import NotificationsSubscription from "../../channels/notifications_subscription";

export default class extends Controller {
  static identifier: string;

  static setIdentifier(identifier: string) {
    this.identifier = identifier;
  }
  static values = {
    threadId: String,
    currentUserId: Number,
  };
  static targets = ["typingIndicator"];

  declare readonly threadIdValue: string;
  declare readonly currentUserIdValue: number;
  declare readonly typingIndicatorTarget: HTMLElement;
  declare readonly hasTypingIndicatorTarget: boolean;

  private notificationsSubscription!: NotificationsSubscription;
  private typingUsers: Map<number, string> = new Map();
  private boundCallback: ((data: any) => void) | null = null;

  connect() {
    console.log(
      "Thread messages controller connected for thread:",
      this.threadIdValue,
    );
    this.subscribeToChannel();
  }

  disconnect() {
    if (this.boundCallback && this.notificationsSubscription) {
      this.notificationsSubscription.removeCallback(this.boundCallback);
    }
  }

  subscribeToChannel() {
    this.notificationsSubscription = NotificationsSubscription.getInstance();

    // Create bound callback so we can remove it later
    this.boundCallback = (data: any): void => {
      console.log("Received message via ActionCable:", data);

      if (data.message && this.isMessageForCurrentThread(data.message)) {
        this.insertMessage(data.message);
      } else if (data.typing && data.typing.thread_id === this.threadIdValue) {
        this.handleTypingIndicator(data.typing);
      } else if (
        data.read_status &&
        data.read_status.thread_id === this.threadIdValue
      ) {
        this.updateReadReceipts(data.read_status);
      }
    };

    this.notificationsSubscription.addCallback(this.boundCallback);
  }

  isMessageForCurrentThread(message: any): boolean {
    // Check if the message belongs to the current thread
    return (
      message.message_thread_encoded_id === this.threadIdValue ||
      message.message_thread_id === this.threadIdValue ||
      message.thread_id === this.threadIdValue ||
      (message.message_thread &&
        message.message_thread.encoded_id === this.threadIdValue)
    );
  }

  handleTypingIndicator(typing: any) {
    const userId = typing.user_id;
    const userName = typing.user_name || "Someone";
    const isTyping = typing.is_typing;

    console.log("Typing indicator received:", {
      userId,
      userName,
      isTyping,
      currentUserId: this.currentUserIdValue,
      threadId: typing.thread_id,
      currentThreadId: this.threadIdValue,
    });

    // Don't show typing indicator for current user
    if (userId === this.currentUserIdValue) {
      console.log("Ignoring typing indicator from current user");
      return;
    }

    if (isTyping) {
      this.typingUsers.set(userId, userName);
      console.log(
        "Added typing user:",
        userName,
        "Total typing:",
        this.typingUsers.size,
      );
    } else {
      this.typingUsers.delete(userId);
      console.log(
        "Removed typing user:",
        userName,
        "Total typing:",
        this.typingUsers.size,
      );
    }

    this.updateTypingIndicator();
  }

  updateTypingIndicator() {
    if (!this.hasTypingIndicatorTarget) {
      console.log("No typing indicator target found!");
      return;
    }

    if (this.typingUsers.size === 0) {
      console.log("Clearing typing indicator");
      this.typingIndicatorTarget.textContent = "\u00A0"; // Non-breaking space to maintain height
      return;
    }

    const names = Array.from(this.typingUsers.values());
    let text = "";

    if (names.length === 1) {
      text = `${names[0]} is typing...`;
    } else if (names.length === 2) {
      text = `${names[0]} and ${names[1]} are typing...`;
    } else {
      text = `${names[0]} and ${names.length - 1} others are typing...`;
    }

    console.log("Showing typing indicator:", text);
    this.typingIndicatorTarget.textContent = text;
  }

  updateReadReceipts(readStatusData: any) {
    console.log("Updating read receipts:", readStatusData);

    // Find all messages from the current user that are older than the read timestamp
    const messagesContainer = this.element.querySelector(".overflow-y-auto");
    if (!messagesContainer) return;

    // Find all current user's messages with unread checkmarks (gray single check)
    const unreadMessages = messagesContainer.querySelectorAll(
      ".flex.items-start.justify-end",
    );

    unreadMessages.forEach((messageElement) => {
      // Find the gray single checkmark SVG
      const grayCheckmark = messageElement.querySelector("svg.text-gray-400");
      if (!grayCheckmark) return; // Already read or no checkmark

      // Replace with green double checkmark
      const readCheckmarkHTML = `
        <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13l4 4L23 7" opacity="0.6"></path>
        </svg>
      `;

      grayCheckmark.outerHTML = readCheckmarkHTML;
    });
  }

  insertMessage(messageData: any) {
    // Skip "messages_seen" system messages - we show read receipts instead
    const isSystemMessage =
      messageData.type === "EventMessage" || messageData.is_system_message;
    if (isSystemMessage && messageData.event_type === "messages_seen") {
      return;
    }

    const encodedId = messageData.encoded_id;
    if (!encodedId) {
      return;
    }

    const messagesContainer = this.element.querySelector(".overflow-y-auto");
    if (!messagesContainer) {
      return;
    }

    // Dedupe: if this message is already in the DOM (e.g. it was on the page
    // at load, or arrived via a duplicate broadcast), don't insert it again.
    const selector = `[data-message-encoded-id="${CSS.escape(encodedId)}"]`;
    if (messagesContainer.querySelector(selector)) {
      return;
    }

    // Fetch the server-rendered Communico::Messaging::Message component so the
    // realtime bubble matches a page-load bubble exactly (same colors, avatar,
    // timestamp, read receipts), instead of hand-building stale markup here.
    fetch(`/account/messages/messages/${encodeURIComponent(encodedId)}`, {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (html == null) {
          return;
        }
        // Re-check after the async gap in case a concurrent insert won.
        if (messagesContainer.querySelector(selector)) {
          return;
        }

        const wrapper = document.createElement("div");
        wrapper.setAttribute("data-message-encoded-id", encodedId);
        wrapper.innerHTML = html.trim();
        messagesContainer.appendChild(wrapper);

        requestAnimationFrame(() => {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        });
      })
      .catch((error) => {
        console.error("Failed to fetch rendered message:", error);
      });
  }
}
